import { getConsent } from "./consent";
import { sendFunnelEventToBackend } from "./backend";

const DEFAULT_MEASUREMENT_ID = "G-GCXRQESRZD";
let initialized = false;
const isDev = process.env.NODE_ENV !== "production";

function hasWindow() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function isGranted() {
  return getConsent() === "granted";
}

function debugLog(action, payload = {}) {
  if (!isDev) return;
  // DEV-only smoke visibility for analytics events.
  console.debug("[analytics]", action, payload);
}

function readStorage(key) {
  if (!hasWindow()) return "";
  try {
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function parseCountryIdFromStorage() {
  const locationRaw = readStorage("incooking_location_v1");
  if (locationRaw) {
    try {
      const parsed = JSON.parse(locationRaw);
      const id = Number(parsed?.country);
      if (id === 1 || id === 2) return id;
    } catch {
      // ignore
    }
  }
  const selected = Number(readStorage("selected_country"));
  if (selected === 1 || selected === 2) return selected;
  return null;
}

function parseLanguageContext() {
  const stored = (readStorage("language") || "").trim();
  if (stored) return stored;
  if (hasWindow()) {
    const m = (window.location.pathname || "").match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i);
    if (m && m[1]) return m[1];
    const htmlLang = document?.documentElement?.getAttribute("lang") || "";
    if (htmlLang) return htmlLang;
  }
  return "en";
}

function parseIsGuest() {
  const token = readStorage("auth_token");
  return !token;
}

function parseUserTier(isGuest) {
  const explicit = (readStorage("incooking_user_tier") || "").trim().toLowerCase();
  if (explicit) return explicit;
  return isGuest ? "guest" : "free";
}

function enrichEventParams(params = {}) {
  const is_guest = parseIsGuest();
  const country_id = parseCountryIdFromStorage();
  const language = parseLanguageContext();
  const user_tier = parseUserTier(is_guest);
  return {
    user_tier,
    is_guest,
    country_id,
    language,
    ...params,
  };
}

export function getGaMeasurementId() {
  return (
    process.env.REACT_APP_GA_MEASUREMENT_ID ||
    process.env.REACT_APP_GA4_MEASUREMENT_ID ||
    DEFAULT_MEASUREMENT_ID
  );
}

export function safeGtag(...args) {
  if (!hasWindow() || typeof window.gtag !== "function") return false;
  window.gtag(...args);
  return true;
}

export function hasAnalytics() {
  return hasWindow() && isGranted() && initialized && typeof window.gtag === "function";
}

export function initGtag(measurementId) {
  if (!hasWindow()) return false;
  const resolvedMeasurementId = measurementId || getGaMeasurementId();
  if (!resolvedMeasurementId || !isGranted()) return false;

  if (typeof window.gtag !== "function") return false;
  if (!initialized) {
    initialized = true;
    debugLog("init", { measurementId: resolvedMeasurementId, bootstrap: "index.html" });
  }
  return true;
}

export function trackEvent(name, params = {}) {
  if (!hasWindow()) return;
  if (!isGranted()) return;
  const enrichedParams = enrichEventParams(params);
  sendFunnelEventToBackend(name, enrichedParams);
  if (!initialized) return;
  debugLog("event", { name, params: enrichedParams });
  safeGtag("event", name, enrichedParams);
}

export function trackPageView(path, title) {
  if (!hasWindow()) return;
  if (!initialized || !isGranted()) return;
  const page_path = path || `${window.location.pathname}${window.location.search}`;
  debugLog("page_view", { page_path });
  safeGtag("event", "page_view", enrichEventParams({
    page_path,
    page_location: window.location.href,
    page_title: title || document.title,
  }));
}

export function setUserId(userId) {
  if (!hasAnalytics()) return false;
  const id = String(userId || "").trim();
  if (!id) return false;
  return safeGtag("set", { user_id: id });
}

export function setUserProps(user = {}) {
  if (!hasAnalytics()) return false;
  if (!user || typeof user !== "object") return false;
  return safeGtag("set", "user_properties", user);
}
